import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Table2 } from "lucide-react";
import { NewSheetDialog } from "@/components/production/NewSheetDialog";
import { STATUS } from "@/components/production/sheetModel";

export default function ProductionSheetsPage() {
  const navigate = useNavigate();
  const [sheets, setSheets] = useState([]);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);

  useEffect(() => { axios.get(`${API}/production-sheets`).then(r => setSheets(r.data || [])).catch(() => setSheets([])); }, []);
  const s = q.toLowerCase();
  const visible = sheets.filter(x => !s || [x.sheet_number, x.title, x.customer_name, x.project_name, x.estimate_number].filter(Boolean).some(v => v.toLowerCase().includes(s)));

  return (
    <div className="space-y-6" data-testid="production-sheets-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Production Sheets</h1>
          <p className="text-sm text-slate-500">Excel-style cut lists for boxes, doors and mouldings — split-dispatched to suppliers as unbranded PDFs.</p>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="new-sheet-btn"><Plus className="w-4 h-4 mr-2" /> New Sheet</Button>
      </div>
      <Input className="w-64" placeholder="Search sheet, customer, project…" value={q} onChange={e => setQ(e.target.value)} data-testid="sheet-search" />
      <Card>
        <CardContent className="p-0">
          {visible.length === 0 ? (
            <div className="text-center py-12"><Table2 className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="text-slate-500">No production sheets yet</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 text-left"><tr><th className="p-3">Sheet</th><th className="p-3">Customer / Project</th><th className="p-3 text-right">Rows</th><th className="p-3 text-right">Pieces</th><th className="p-3 text-right">Paint SqFt</th><th className="p-3 text-right">Dispatches</th><th className="p-3">Status</th></tr></thead>
                <tbody>
                  {visible.map(x => {
                    const st = STATUS[x.status] || STATUS.draft;
                    return (
                      <tr key={x.sheet_id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/production-sheets/${x.sheet_id}`)} data-testid={`sheet-row-${x.sheet_id}`}>
                        <td className="p-3"><p className="font-semibold">{x.sheet_number}{x.mode === "scratchpad" && <span className="ml-2 rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-medium text-white" data-testid={`scratchpad-badge-${x.sheet_id}`}>Scratchpad</span>}</p><p className="text-xs text-slate-500 truncate max-w-[220px]">{x.title}</p></td>
                        <td className="p-3"><p>{x.customer_name || "—"}</p><p className="text-xs text-slate-500">{x.project_name || (x.estimate_number ? `from ${x.estimate_number}` : "")}</p></td>
                        <td className="p-3 text-right">{x.totals?.rows ?? 0}</td>
                        <td className="p-3 text-right">{x.totals?.quantity ?? 0}</td>
                        <td className="p-3 text-right">{(x.totals?.paint_sqft ?? 0).toFixed(2)}</td>
                        <td className="p-3 text-right">{(x.dispatches || []).length}</td>
                        <td className="p-3"><span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <NewSheetDialog open={showNew} onOpenChange={setShowNew} />
    </div>
  );
}
